import {
    differenceInCalendarDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from 'date-fns';

export enum COMPARISON_RANGE_KEY {
    PREVIOUS_YEAR_EQUIVALENT = 'previousYearEquivalent',
    PREVIOUS_PERIOD_EQUIVALENT = 'previousPeriodEquivalent',
    PREVIOUS_WEEK = 'previousWeek',
    PREVIOUS_MONTH = 'previousMonth',
    PREVIOUS_YEAR = 'previousYear',
}

export interface ComparisonRange {
    startDate: Date
    endDate: Date
}

interface MainRange {
    startDate?: Date | null
    endDate?: Date | null
}

// Weeks start on Monday.
const WEEK_OPTIONS = { weekStartsOn: 1 as const };

/**
 * Comparison period derived from the selected one.
 *
 * Returns null when no period is selected, and for an unknown key. The start is
 * always the very beginning of its day and the end the very end of its own, so
 * the range covers whole days whatever the time the picker carries.
 */
export const getComparisonRange = (
    key: string,
    mainRange?: MainRange | null,
): ComparisonRange | null => {
    if (!mainRange?.startDate) {
        return null;
    }
    // A period reduced to a single day comes with no end date.
    const from = startOfDay(mainRange.startDate);
    const to = endOfDay(mainRange.endDate ?? mainRange.startDate);

    switch (key) {
        case COMPARISON_RANGE_KEY.PREVIOUS_YEAR_EQUIVALENT:
            // subYears falls back on the last day of the month when the day does
            // not exist a year earlier, which is what 29 February needs.
            return {
                startDate: startOfDay(subYears(from, 1)),
                endDate: endOfDay(subYears(to, 1)),
            };
        case COMPARISON_RANGE_KEY.PREVIOUS_PERIOD_EQUIVALENT: {
            const lengthInDays = differenceInCalendarDays(to, from) + 1;

            return {
                startDate: startOfDay(subDays(from, lengthInDays)),
                endDate: endOfDay(subDays(from, 1)),
            };
        }
        case COMPARISON_RANGE_KEY.PREVIOUS_WEEK: {
            const previousWeek = subWeeks(from, 1);

            return {
                startDate: startOfDay(startOfWeek(previousWeek, WEEK_OPTIONS)),
                endDate: endOfDay(endOfWeek(previousWeek, WEEK_OPTIONS)),
            };
        }
        case COMPARISON_RANGE_KEY.PREVIOUS_MONTH: {
            const previousMonth = subMonths(from, 1);

            return {
                startDate: startOfDay(startOfMonth(previousMonth)),
                endDate: endOfDay(endOfMonth(previousMonth)),
            };
        }
        case COMPARISON_RANGE_KEY.PREVIOUS_YEAR: {
            const previousYear = subYears(from, 1);

            return {
                startDate: startOfDay(startOfYear(previousYear)),
                endDate: endOfDay(endOfYear(previousYear)),
            };
        }
        default:
            return null;
    }
};

export default getComparisonRange;
